import Chart, { ChartOptions } from "chart.js/auto";
import React, { useEffect, useMemo, useRef } from "react";

import {
  INextGenDashboardChart,
  INextGenDashboardChartSeries,
  NextGenChartAxisFormat,
  NextGenChartType,
} from "../../services/zeos-api/contracts/Dashboard";
import {
  FALLBACK_SERIES_COLORS,
  NEXT_GEN_AXIS_STYLE,
  NEXT_GEN_CHART_BASE_OPTIONS,
  NEXT_GEN_CHART_DATASET_DEFAULTS,
  SERIES_COLORS,
  SERIES_STYLES,
} from "./nextGenChartTheme";

interface IProps {
  chart: INextGenDashboardChart | null;
}

// Driven by the chart's declared xAxisFormat, never by its axis *title*: every chart titles its
// x-axis "Month", so keying off that stripped the year from all of them and made any range longer
// than 12 months repeat ambiguous month names.
const formatXAxisLabel = (
  dateValue: string,
  format?: NextGenChartAxisFormat | null
) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  if (format === "week") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  // Month-only is correct solely for a same-calendar-month comparison, where the year is carried
  // by the series name and every point shares one nominal year.
  if (format === "month") {
    return date.toLocaleDateString("en-US", { month: "short" });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
};


/** `#rrggbb` at the given opacity; any other colour string is returned unchanged. */
export const withAlpha = (color: string, alpha: number) => {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (!match) {
    return color;
  }
  const [r, g, b] = match.slice(1).map((hex) => parseInt(hex, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Months with at least one estimated point (a vendor outage month filled from last year's
 * production), oldest first, as "Nov 2025".
 */
export const estimatedMonthLabels = (chart: INextGenDashboardChart | null) => {
  if (!chart) return [];
  const dates = new Set<string>();
  chart.series.forEach((series) =>
    series.points.forEach((point) => {
      if (point.isEstimated) dates.add(point.date);
    })
  );
  // Read the month off the string: the API sends a zone-less "2025-11-01T00:00:00", which a
  // browser east of UTC would otherwise place in the previous month.
  return Array.from(dates)
    .sort()
    .map((date) => {
      const [year, month] = date.slice(0, 7).split("-").map(Number);
      return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    });
};

const baseChartJsConfig = (type: NextGenChartType) => {
  if (type === "line") {
    return { chartJsType: "line" as const, stacked: false };
  }
  return { chartJsType: "bar" as const, stacked: type === "stacked_bar" };
};

const resolveSeriesChartJsType = (
  chart: INextGenDashboardChart,
  series: INextGenDashboardChartSeries
): "line" | "bar" => {
  if (series.type === "line" || series.type === "bar") {
    return series.type;
  }
  return baseChartJsConfig(chart.type).chartJsType;
};

const buildDataset = (
  chart: INextGenDashboardChart,
  series: INextGenDashboardChartSeries,
  index: number,
  data: (number | null)[],
  estimated: boolean[]
) => {
  const color =
    SERIES_COLORS[series.name] ??
    FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length];
  const style = SERIES_STYLES[series.name] ?? { borderColor: color, backgroundColor: color };
  const type = resolveSeriesChartJsType(chart, series);

  return {
    type,
    label: series.name,
    data,
    ...NEXT_GEN_CHART_DATASET_DEFAULTS,
    ...style,
    ...(type === "bar" ? { borderRadius: 3, maxBarThickness: 40 } : {}),
    ...(estimated.some(Boolean) ? estimatedMarkers(type, style, estimated) : {}),
    // Read back by the tooltip to say which values are estimates.
    estimated,
    // Lines draw over bars in combo charts.
    order: type === "line" ? 0 : 1,
    yAxisID: series.axisId === "secondary" ? "y1" : "y",
  };
};

// Estimated months read as "not measured": a line goes dashed into and out of the point and the
// point is hollow; a bar is a pale fill with a solid outline.
const estimatedMarkers = (
  type: "line" | "bar",
  style: { borderColor: string; backgroundColor: string; borderWidth?: number; borderDash?: number[] },
  estimated: boolean[]
) => {
  const isEstimated = (i: number) => estimated[i] === true;
  if (type === "line") {
    return {
      segment: {
        borderDash: (ctx: { p0DataIndex: number; p1DataIndex: number }) =>
          isEstimated(ctx.p0DataIndex) || isEstimated(ctx.p1DataIndex) ? [4, 4] : style.borderDash ?? [],
      },
      pointBackgroundColor: (ctx: { dataIndex: number }) =>
        isEstimated(ctx.dataIndex) ? "#ffffff" : style.backgroundColor,
      pointBorderColor: style.borderColor,
      pointBorderWidth: (ctx: { dataIndex: number }) => (isEstimated(ctx.dataIndex) ? 1.5 : 1),
      pointRadius: (ctx: { dataIndex: number }) =>
        isEstimated(ctx.dataIndex) ? 3.5 : NEXT_GEN_CHART_DATASET_DEFAULTS.pointRadius,
    };
  }
  return {
    backgroundColor: (ctx: { dataIndex: number }) =>
      isEstimated(ctx.dataIndex) ? withAlpha(style.backgroundColor, 0.3) : style.backgroundColor,
    borderColor: style.borderColor,
    borderWidth: (ctx: { dataIndex: number }) =>
      isEstimated(ctx.dataIndex) ? 1.5 : style.borderWidth ?? 0,
  };
};

export const NextGenChart = ({ chart }: IProps) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart | null>(null);


  const labelDates = useMemo(() => {
    if (!chart) return [];
    const dates = new Set<string>();
    chart.series.forEach((series) =>
      series.points.forEach((point) => dates.add(point.date))
    );
    return Array.from(dates).sort();
  }, [chart]);

  const labels = useMemo(() => {
    if (!chart) return [];
    return labelDates.map((date) => formatXAxisLabel(date, chart.xAxisFormat));
  }, [chart, labelDates]);

  const datasets = useMemo(() => {
    if (!chart) return [] as any[];
    return chart.series.map((series, index) => {
      const valueByDate = new Map(series.points.map((point) => [point.date, point.value]));
      const data = labelDates.map((date) => valueByDate.get(date) ?? null);
      const estimatedDates = new Set(
        series.points.filter((point) => point.isEstimated).map((point) => point.date)
      );
      const estimated = labelDates.map((date) => estimatedDates.has(date));
      return buildDataset(chart, series, index, data, estimated);
    });
  }, [chart, labelDates]);

  const estimatedMonths = useMemo(() => estimatedMonthLabels(chart), [chart]);

  useEffect(() => {
    if (!chart || !chartRef.current) {
      return undefined;
    }

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) {
      return undefined;
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const { chartJsType, stacked } = baseChartJsConfig(chart.type);
    const hasSecondaryAxis = chart.series.some((series) => series.axisId === "secondary");

    const options: ChartOptions = {
      ...NEXT_GEN_CHART_BASE_OPTIONS,
      plugins: {
        ...NEXT_GEN_CHART_BASE_OPTIONS.plugins,
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const estimated = (ctx.dataset as { estimated?: boolean[] }).estimated;
              const text = `${ctx.dataset.label}: ${ctx.formattedValue}`;
              return estimated?.[ctx.dataIndex] ? `${text} (estimated)` : text;
            },
          },
        },
      },
      scales: {
        y: {
          ...NEXT_GEN_AXIS_STYLE,
          beginAtZero: false,
          stacked,
          title: {
            ...NEXT_GEN_AXIS_STYLE.title,
            display: true,
            text: chart.yAxisLabel,
          },
        },
        x: {
          ...NEXT_GEN_AXIS_STYLE,
          stacked,
          grid: { display: false },
          title: {
            ...NEXT_GEN_AXIS_STYLE.title,
            display: true,
            text: chart.xAxisLabel,
          },
        },
        ...(hasSecondaryAxis
          ? {
              y1: {
                ...NEXT_GEN_AXIS_STYLE,
                beginAtZero: false,
                position: "right" as const,
                grid: { drawOnChartArea: false },
                title: {
                  ...NEXT_GEN_AXIS_STYLE.title,
                  display: true,
                  text: chart.secondaryYAxisLabel ?? "",
                },
              },
            }
          : {}),
      },
    };

    chartInstance.current = new Chart(ctx, {
      type: chartJsType,
      data: {
        labels,
        datasets,
      },
      options,
    });

    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, [chart, labels, datasets]);

  if (!chart) {
    return null;
  }

  return (
    <>
      <div className="h-[320px] md:h-[380px]">
        <canvas ref={chartRef} />
      </div>
      {estimatedMonths.length ? (
        <p className="mt-2 text-[11px] font-semibold text-mf-faint">
          Dashed or hollow marks are estimates for {estimatedMonths.join(", ")}: the solar
          vendor reported no data, so these months use last year&apos;s production, scaled. Real
          data replaces them if it arrives.
        </p>
      ) : null}
    </>
  );
};
